import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { User, Lock, LogIn, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerLogin = () => {
  const navigate = useNavigate();
  const [accountNumber, setAccountNumber] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      setLoadingInfo(true);
      // Try to get company/tenant info
      const response = await axios.get(`${API_URL}/api/portal/T06122503/info`);
      if (response.data.success) {
        setCompanyInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
      // Use default values if API fails
      setCompanyInfo({
        tenant_name: '',
        branding: {
          logo_url: '',
          primary_color: '#3b82f6'
        },
        support_phone: '+62 821-1239-3993'
      });
    } finally {
      setLoadingInfo(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!accountNumber || !password) {
      toast.error('Mohon isi ID Pelanggan dan Password');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/v2/customer/login`, {
        account_number: accountNumber,
        password: password
      });

      if (response.data.success) {
        const token = response.data.data.access_token;
        const customerData = response.data.data.customer;
        
        localStorage.setItem('customer_token', token);
        localStorage.setItem('customer_data', JSON.stringify(customerData));
        
        const storedToken = localStorage.getItem('customer_token');
        if (!storedToken) {
          console.error('Failed to save token to localStorage');
          toast.error('Terjadi kesalahan saat menyimpan data login');
          return;
        }
        
        toast.success(`Selamat datang, ${customerData.name}!`);
        
        await new Promise(resolve => setTimeout(resolve, 50));
        
        navigate('/customer/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login gagal. Periksa ID Pelanggan dan Password Anda.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: companyInfo?.branding?.primary_color 
          ? `linear-gradient(135deg, ${companyInfo.branding.primary_color}15, white, ${companyInfo.branding.primary_color}10)`
          : 'linear-gradient(135deg, #3b82f615, white, #8b5cf610)'
      }}
    >
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          {(companyInfo?.branding?.logo_portal_url || companyInfo?.branding?.logo_url) ? (
            <div className="flex justify-center mb-3">
              <img 
                src={companyInfo.branding.logo_portal_url || companyInfo.branding.logo_url || ''} 
                alt={companyInfo.tenant_name}
                className="h-16 w-auto object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          ) : (
            <div className="flex justify-center mb-3">
              <img 
                src="" 
                alt="Logo"
                className="h-16 w-auto object-contain"
              />
            </div>
          )}
          <h1 className="text-xl font-bold text-gray-900 dark:text-slate-100">{companyInfo?.tenant_name || 'Portal Pelanggan'}</h1>
          <p className="text-gray-600 text-sm dark:text-slate-300">Portal Pelanggan</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100 dark:bg-slate-800 dark:border-slate-700">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center dark:text-slate-200">
            Masuk ke Akun Anda
          </h2>

          <form onSubmit={handleLogin} className="space-y-5">
            {/* Account Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                ID Pelanggan
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="Contoh: 25112000001"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-blue-50/30 dark:border-slate-700"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Masukkan password Anda"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-blue-50/30 dark:border-slate-700"
                  disabled={loading}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                Password default: 12345678
              </p>
            </div>

            {/* Login Button - Dark style like SS1 */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gray-800 hover:bg-gray-900 text-white font-semibold py-3 rounded-lg flex items-center justify-center space-x-2 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Memproses...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>Masuk</span>
                </>
              )}
            </button>
          </form>

          {/* Help Text with CS Number */}
          <div className="mt-6 pt-6 border-t border-gray-200 text-center dark:border-slate-700">
            <p className="text-sm text-gray-600 dark:text-slate-300">
              Lupa ID Pelanggan atau Password?
            </p>
            <p className="text-sm text-gray-600 mt-1 dark:text-slate-300">
              Hubungi Customer Service
            </p>
            {companyInfo?.support_phone && (
              <p className="text-base font-semibold text-gray-800 mt-2 dark:text-slate-200">
                {companyInfo.support_phone}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500 dark:text-slate-400">
            © {new Date().getFullYear()} {companyInfo?.tenant_name || ''}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;
